import { useSignIn } from '@clerk/clerk-react';
import { EmailLinkFactor, SignInFirstFactor } from '@clerk/types';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router';

import { A11yButton } from '~components/commons/buttons/A11yButton';
import { Button } from '~components/commons/buttons/Button';
import TextFieldInput from '~components/commons/textField/textFieldInput';
import AuthLayoutWithFrame from '~components/login/AuthLayout/AuthLayoutWithFrame';

import { EmailBoldIcon, Logo } from '~assets/images';
import type { ClerkError } from '~types/clerk';

import styles from './EmailLink.module.scss';

interface EmailLinkProps {
	defaultEmailAddress: string;
}

export const EmailLink: React.FC<EmailLinkProps> = ({ defaultEmailAddress }) => {
	const [isVerified, setIsVerified] = useState(false);
	const [hasLinkBeenSent, setHasLinkBeenSent] = useState(false);
	const [isVerifying, setIsVerifying] = useState(false);
	const [emailAddress, setEmailAddress] = useState(defaultEmailAddress || '');
	const [error, setError] = useState('');

	const { signIn, isLoaded: isSignInLoaded, setActive } = useSignIn();

	const sendEmailLink = useCallback(async (): Promise<void> => {
		setIsVerified(false);
		setError('');

		if (isVerifying && !isSignInLoaded && !emailAddress) {
			return;
		}

		try {
			if (signIn) {
				const { supportedFirstFactors } = await signIn.create({
					identifier: emailAddress,
				});

				setIsVerifying(true);

				const emailLinkFactor = supportedFirstFactors?.find(
					(factor: SignInFirstFactor): factor is EmailLinkFactor =>
						factor.strategy === 'email_link',
				);

				if (!emailLinkFactor) {
					setError('Email link factor not found');
					return;
				}

				const { emailAddressId } = emailLinkFactor;

				const { protocol } = window.location;
				const { host } = window.location;

				const { startEmailLinkFlow } = signIn.createEmailLinkFlow();

				setHasLinkBeenSent(true);
				const signInAttempt = await startEmailLinkFlow({
					emailAddressId,
					redirectUrl: `${protocol}//${host}/email-link/verify?emailAddress=${encodeURIComponent(
						emailAddress,
					)}`,
				});

				const verification = signInAttempt.firstFactorVerification;

				if (verification.status === 'expired') {
					setError('The email link has expired.');
					return;
				}

				if (verification.verifiedFromTheSameClient()) {
					setActive({ session: signInAttempt.createdSessionId });
					setIsVerified(true);
				}
			}
		} catch (err) {
			Sentry.captureException(err);
			setError((err as ClerkError).errors[0].longMessage || 'Something went wrong');
		} finally {
			setIsVerifying(false);
		}
	}, [emailAddress, isVerifying, isSignInLoaded, signIn, setActive]);

	useEffect(() => {
		if (defaultEmailAddress) {
			setEmailAddress(defaultEmailAddress);
		}
	}, [defaultEmailAddress]);

	return (
		<AuthLayoutWithFrame>
			<div className={styles.emailLinkContainer}>
				<Logo className={styles.logo} />
				<div className={styles.cardContainer}>
					{isVerified && (
						<div className={styles.headerTitle}>
							<h2>Signed in successfully!</h2>
						</div>
					)}

					{!isVerified && (
						<React.Fragment>
							<div className={styles.headerTitle}>
								<h2>Welcome back</h2>
								<p>Log in to your Cents Business Manager to continue</p>
							</div>

							{!hasLinkBeenSent && (
								<React.Fragment>
									<div className={styles.inputContainer}>
										<TextFieldInput
											value={emailAddress}
											onChange={(
												e: React.ChangeEvent<HTMLInputElement>,
											) => setEmailAddress(e.target.value)}
											label="Email address"
											type="email"
											suffix={
												<EmailBoldIcon
													className={styles.textFieldIcon}
												/>
											}
											className={styles.input}
										/>
										{error && <p className={styles.error}>{error}</p>}
									</div>
									<Button
										onClick={sendEmailLink}
										disabled={emailAddress.trim().length === 0}
									>
										Continue
									</Button>
								</React.Fragment>
							)}

							{hasLinkBeenSent && (
								<React.Fragment>
									<div className={styles.checkEmailContainer}>
										<div className={styles.headerContainer}>
											<EmailBoldIcon
												className={styles.checkEmailIcon}
											/>
											<p>Check your email inbox</p>
										</div>
										<div className={styles.checkEmailDescription}>
											<p>
												To log in, click the magic link in the
												email we sent to{' '}
												<span>{emailAddress}</span>
											</p>
											{error && (
												<p className={styles.error}>{error}</p>
											)}
										</div>
										<div className={styles.checkEmailButtons}>
											<A11yButton
												onAction={() => {
													toast.success('Email link resent');
													sendEmailLink();
												}}
												className={styles.button}
											>
												Resend email
											</A11yButton>
											<Link to="/login">Switch account</Link>
										</div>
									</div>
								</React.Fragment>
							)}
						</React.Fragment>
					)}
				</div>
			</div>
		</AuthLayoutWithFrame>
	);
};
